import { useLocation } from "react-router-dom";
import { Icon } from "@/components/ui/Icon";
import { useSpace } from "@/hooks/useSpace";
import { BIZ_META, SPACE_META, TENANT_BIZ, bizGate } from "@/domain/space";

/**
 * SpaceSwitch — 헤더 왼쪽 공간 전환 (B′, PC·fold).
 *
 *   [⌂ 내 업무] │ [● 중앙산업][● 경원산업]
 *
 * - 선택 표시: 밑줄 없이 굵은 글씨 + 배경. 내 업무 = 중립 회색, 법인 = 법인 색 연한 배경 + 법인 색 글자.
 * - 한 법인 소속: 세그먼트 대신 그 법인 칩 하나(.sp-seg.is-single — 틀 없음).
 * - 기사(근무 법인 없음): 내 업무 칩만, 구분선도 없음.
 * - fold(660~1023): 법인 이름은 짧은 이름(중앙/경원) — CSS 가 .sp-name-full/.sp-name-short 를 바꿔 끼움.
 * - MO(≤659): 같은 컴포넌트를 그대로 노출 — [⌂][중앙][경원]. 내 업무는 아이콘만(CSS), 탭하면 바로 전환.
 * 드롭다운·▾·펼침 메뉴는 붙이지 않는다(2026-10-19 피드백 — 누르면 바로 전환, 선택만 굵게+배경).
 *
 * 사업(D-26, 2026-10-22): 사업이 2개 이상인 법인은 법인 칩 바로 옆에 사업 버튼을 붙인다(같은 칸 안, 펼침 없음).
 *   [● 중앙산업 │ 레미콘 · 석산] [● 경원산업]
 *   - 법인 칩 = 그 법인으로 전환(기본 사업 또는 같은 메뉴), 사업 버튼 = 같은 법인 안에서 사업 전환.
 *   - 지금 사업이 굵게. 법인 공통 메뉴(급여·원가·설정)를 보고 있으면 사업 버튼은 모두 보통 굵기.
 *   - 준비중 사업(ready=false)은 흐린 글자 + title 안내.
 *   - 사업 권한(BIZ_META.gate — 석산 `aggregate.view`)이 없는 사업 버튼은 아예 안 보인다(2026-10-29). 남는 사업이 하나면 법인 칩만.
 */
export function SpaceSwitch() {
  const { space: ctxSpace, biz, tenants, switchSpace, switchBiz, hasPermIn } = useSpace();
  // 통합관리(/group, 법인 공간 밖)에서는 아무 칸도 선택하지 않는다(설계 12 §8-B). 누르면 그 공간 홈으로.
  const { pathname } = useLocation();
  const outside = pathname === "/group" || pathname.startsWith("/group/");
  const space = outside ? null : ctxSpace;
  return (
    <div className="sp-switch">
      <button
        type="button"
        className={`sp-chip sp-my${space === "my" ? " is-active" : ""}`}
        aria-pressed={space === "my"}
        aria-label={SPACE_META.my.label}
        onClick={() => switchSpace("my")}
      >
        <Icon name="home" size={16} />
        <span className="sp-label sp-my-label" data-label={SPACE_META.my.label}>{SPACE_META.my.label}</span>
      </button>
      {tenants.length > 0 && (
        <>
          <span className="sp-sep" aria-hidden="true" />
          <div
            className={`sp-seg${tenants.length === 1 ? " is-single" : ""}`}
            role="group"
            aria-label="법인 선택"
          >
            {tenants.map((t) => {
              const m = SPACE_META[t];
              const active = space === t;
              const bizList = TENANT_BIZ[t].filter((b) => { const g = bizGate(b); return !g || hasPermIn(g, t); });
              const chip = (
                <button
                  key={t}
                  type="button"
                  className={`sp-chip sp-tenant is-space-${t}${active ? " is-active" : ""}`}
                  aria-pressed={active}
                  aria-label={m.label}
                  onClick={() => switchSpace(t)}
                >
                  <span className="sp-dot" aria-hidden="true" />
                  {/* data-label: 굵게 바뀌어도 폭이 흔들리지 않게 굵은 글자 폭을 미리 잡아 둠(CSS ::after) */}
                  <span className="sp-label sp-name-full" data-label={m.label}>{m.label}</span>
                  <span className="sp-label sp-name-short" data-label={m.short} aria-hidden="true">{m.short}</span>
                </button>
              );
              if (bizList.length < 2) return chip;
              return (
                <div key={t} className={`sp-tenant-group is-space-${t}${active ? " is-active" : ""}`}>
                  {chip}
                  <div className="sp-biz" role="group" aria-label={`${m.short} 사업 선택`}>
                    {bizList.map((b) => {
                      const bm = BIZ_META[b];
                      const on = active && biz === b;
                      return (
                        <button
                          key={b}
                          type="button"
                          className={`sp-biz-btn${on ? " is-active" : ""}${bm.ready ? "" : " is-pending"}`}
                          aria-pressed={on}
                          title={bm.ready ? `${m.short} ${bm.label}` : `${bm.label} — 별도 ERP 구축 후 연결(준비중)`}
                          onClick={() => switchBiz(t, b)}
                        >
                          <span className="sp-label" data-label={bm.label}>{bm.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
